import { createSlice } from "@reduxjs/toolkit";
import {groupBy} from "@/helpers/object";
import dayjs from "dayjs";

export const calendarSlice = createSlice({
  name: "calendar",
  initialState: {
    viewType: "calendar",
    calendarData: [],
    calendarBookingGroups: {},
    editBookingData: null,
    staffOptions: [],
    filteredStaffIds: [],
  },
  reducers: {
    setViewType: (state, { payload }) => {
        state.viewType = payload;
    },
    setCalendarData(state, { payload }) {
      state.calendarData = payload
      state.calendarBookingGroups = groupBy(
        payload,
        (payload) => payload.booking_id
      );
    },
    simpleSetEditBookingData(state, { payload }) {
      state.editBookingData = payload;
    },
    setEditBookingData(state, { payload }) {
      if (!payload) {
        state.editBookingData = null;
        return;
      }

      const updatedGroup = [
        ...(state.calendarBookingGroups[payload.booking_id] || []),
        payload,
      ];

      state.calendarBookingGroups[payload.booking_id] = Array.from(
        new Map(updatedGroup.map((item) => [item.id, item])).values()
      );

      const { minStart, maxEnd } = (state.calendarBookingGroups[payload?.booking_id] ?? []).reduce((acc, item) => {
        const start = dayjs(item.start_date_time);
        const end = dayjs(item.end_date_time);

        const currentStart = acc.minStart ? dayjs(acc.minStart) : null;
        const currentEnd = acc.maxEnd ? dayjs(acc.maxEnd) : null;

        if (!currentStart || start.isBefore(currentStart)) acc.minStart = start.format('YYYY-MM-DD HH:mm').toString();
        if (!currentEnd || end.isAfter(currentEnd)) acc.maxEnd = end.format('YYYY-MM-DD HH:mm').toString();

        return acc;
      }, { minStart: null, maxEnd: null });

      const customer = payload?.user;
      const orderPayload = payload?.booking?.payment?.order?.items?.[0]?.payload ?? null;

      state.editBookingData = {
        id: payload?.booking_id ?? null,
        status: payload?.booking?.status ?? null,
        type: payload?.booking?.type?.type ?? "admin",
        company_id: payload?.company_id ?? null,
        location_id: payload?.location_id ?? null,
        lang: payload?.locale ?? "en",
        trans: {
          en: {
            first_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.en?.first_name ?? (customer?.first_name ?? "")),
            last_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.en?.last_name ?? (customer?.last_name ?? "")),
              notes: (payload?.booking?.trans?.en?.notes ?? (payload?.booking?.notes ?? "")),
          },
          ru: {
            first_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.ru?.first_name ?? (customer?.first_name ?? "")),
            last_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.ru?.last_name ?? (customer?.last_name ?? "")),
            notes: (payload?.booking?.trans?.ru?.notes ?? (payload?.booking?.notes ?? "")),
          },
          hy: {
            first_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.hy?.first_name ?? (customer?.first_name ?? "")),
            last_name: payload?.booking?.is_anonymous ? undefined : (customer?.trans?.hy?.last_name ?? (customer?.last_name ?? "")),
              notes: (payload?.booking?.trans?.hy?.notes ?? (payload?.booking?.notes ?? "")),
          },
        },
        email: payload?.booking?.is_anonymous ? undefined : customer?.email ?? undefined,
        phone: payload?.booking?.is_anonymous ? undefined : customer?.phone ?? undefined,
        nickname: payload?.booking?.is_anonymous ? undefined : customer?.locationInfo?.length && customer?.locationInfo[0].nickname ? customer?.locationInfo[0].nickname : undefined,
        is_anonymous: payload?.booking?.is_anonymous ?? false,
        end_date_time: maxEnd ?? null,
        start_date_time: minStart ?? null,
        payment: payload?.payment ?? null,
        gift_card: payload?.payment?.giftCardUsage?.giftCardUser?.code ?? null,
        invoiceItem: payload?.invoiceItem ?? null,
        services: Array.from(
            new Set(
                (state.calendarBookingGroups[payload?.booking_id] || [])
                    .map((booking) => {
                        if (!booking?.service?.id) {
                            return {}
                        }
                        const start = dayjs(booking.start_date_time)
                        const end = dayjs(booking.end_date_time)
                        return {
                            'service_id': booking?.service?.id,
                            'duration': end.diff(start, 'minute'),
                            'price': booking?.invoiceItem?.amount ?? booking?.service?.price ?? null,
                        }
                    })
                    .filter((id) => id)
            )
        ),
        [Symbol.for('service_options')]: Array.from(
          new Set(
            (state.calendarBookingGroups[payload?.booking_id] || [])
              .map(({ service }) => service)
              .filter((service) => service)
          )
        ),
        [Symbol.for('staff_options')]: [state.calendarBookingGroups[payload?.booking_id][0]?.staff].filter(staff => staff),
        [Symbol.for('customer_options')]: payload?.booking?.is_anonymous ? [] : customer ? [customer] : [],
        staff_id: payload?.staff?.id ?? null,
        customer_id: payload?.booking?.is_anonymous ? undefined : customer?.id ?? null,
        coupon: orderPayload?.coupon
      };
    },
    setStaffOptions(state, { payload }) {
      state.staffOptions = payload
    },
    setFilteredStaffIds(state, { payload }) {
      state.filteredStaffIds = payload
    }
  },
});

export const { setCalendarData, setStaffOptions, setFilteredStaffIds, setEditBookingData, setViewType, simpleSetEditBookingData } = calendarSlice.actions;
